// components/Survey/SurveyCharts.jsx — per-question result visualisations
import React, { useMemo, useState } from "react";
import { Text } from "@fluentui/react-components";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { Search24Regular, Table24Regular, DataPie24Regular } from "@fluentui/react-icons";
import { C, card } from "./surveyUI";
import { TYPE_META, formatDate } from "./surveyUtils";

// Validated reference palette (dataviz skill): one sequential hue for magnitude,
// blue ↔ red diverging arms with a neutral gray midpoint for agreement scales.
export const VIZ = {
  bar: "#2a78d6",
  barMuted: "#9ec5f4",
  grid: "#EEEDEA",
  axis: "#8A8886",
  pos: ["#86b6ef", "#2a78d6", "#1c5cab"],
  neg: ["#f2a3a2", "#e34948", "#b8302f"],
  mid: "#d6d5d0",
  yes: "#2a78d6",
  no: "#eb6834",
};

// Lowest point → strongest red, highest → strongest blue, gray centre when the count is odd.
// Arms longer than three steps repeat their strongest end.
function divergingColors(n) {
  const half = Math.floor(n / 2);
  const negStrongFirst = [...VIZ.neg].reverse(); // dark → light
  const neg = Array.from({ length: half }, (_, i) => negStrongFirst[Math.max(0, i - Math.max(0, half - 3)) + Math.max(0, 3 - half)] || negStrongFirst[0]);
  const pos = Array.from({ length: half }, (_, i) => VIZ.pos[Math.min(2, i)]);
  return n % 2 ? [...neg, VIZ.mid, ...pos] : [...neg, ...pos];
}

function ChartTooltip({ active, payload, label, unit = "responses" }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 12px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: C.ink, marginBottom: 2 }}>{p.fullLabel || label}</div>
      <div style={{ color: C.ink2 }}>{p.count} {unit}{p.pct !== undefined ? ` · ${p.pct.toFixed(1)}%` : ""}</div>
    </div>
  );
}

// Horizontal labelled bars — direct labels, so identity never depends on colour
function HBarList({ rows, highlightTop = true }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  const top = Math.max(...rows.map((r) => r.count));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((r) => (
        <div key={r.id || r.label} title={`${r.label}: ${r.count} (${r.pct.toFixed(1)}%)`}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: C.ink, fontWeight: highlightTop && r.count === top && top > 0 ? 700 : 400 }}>{r.label}</span>
            <span style={{ color: C.ink2, whiteSpace: "nowrap" }}><strong style={{ color: C.ink }}>{r.pct.toFixed(0)}%</strong> · {r.count}</span>
          </div>
          <div style={{ height: 10, background: "#F1F0EE", borderRadius: 4 }}>
            <div style={{ width: `${(r.count / max) * 100}%`, height: "100%", borderRadius: 4, background: highlightTop && r.count === top && top > 0 ? VIZ.bar : VIZ.barMuted, transition: "width 0.4s ease" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div style={{ minWidth: 90 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.ink, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>}
    </div>
  );
}

function SegmentBar({ segments, height = 26 }) {
  const total = segments.reduce((n, s) => n + s.count, 0) || 1;
  return (
    <div>
      <div style={{ display: "flex", height, borderRadius: 4, overflow: "hidden", gap: 2, background: "#fff" }}>
        {segments.filter((s) => s.count > 0).map((s) => (
          <div key={s.label} title={`${s.label}: ${s.count} (${((s.count / total) * 100).toFixed(1)}%)`} style={{ width: `${(s.count / total) * 100}%`, background: s.color, display: "flex", alignItems: "center", justifyContent: "center", color: s.ink || "#fff", fontSize: 12, fontWeight: 700, minWidth: 4 }}>
            {(s.count / total) * 100 >= 9 ? `${Math.round((s.count / total) * 100)}%` : ""}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8 }}>
        {segments.map((s) => (
          <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.ink2 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} /> {s.label} <strong style={{ color: C.ink }}>{s.count}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function ScaleChart({ summary, question }) {
  const data = summary.dist.map((d) => ({ ...d, fullLabel: `${d.label}${d.value === summary.min && question.minLabel ? ` — ${question.minLabel}` : ""}${d.value === summary.max && question.maxLabel ? ` — ${question.maxLabel}` : ""}` }));
  return (
    <>
      <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginBottom: 14 }}>
        {summary.nps !== undefined ? (
          <Stat label="NPS" value={summary.nps === null ? "—" : summary.nps > 0 ? `+${summary.nps}` : summary.nps} sub="−100 to +100" />
        ) : (
          <Stat label="Average" value={summary.avg === null ? "—" : summary.avg.toFixed(2)} sub={`out of ${summary.max}`} />
        )}
        <Stat label="Median" value={summary.median ?? "—"} />
        {summary.nps !== undefined && <Stat label="Average" value={summary.avg === null ? "—" : summary.avg.toFixed(1)} sub="out of 10" />}
      </div>
      {summary.segments && (
        <div style={{ marginBottom: 16 }}>
          <SegmentBar segments={[
            { label: "Detractors (0–6)", count: summary.segments.detractors, color: VIZ.neg[1] },
            { label: "Passives (7–8)", count: summary.segments.passives, color: VIZ.mid, ink: C.ink },
            { label: "Promoters (9–10)", count: summary.segments.promoters, color: VIZ.bar },
          ]} />
        </div>
      )}
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barCategoryGap="18%">
            <CartesianGrid vertical={false} stroke={VIZ.grid} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: VIZ.axis }} axisLine={{ stroke: VIZ.grid }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: VIZ.axis }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(42,120,214,0.06)" }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56}>
              {data.map((d) => <Cell key={d.value} fill={VIZ.bar} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {(question.minLabel || question.maxLabel) && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted, padding: "0 4px 0 24px" }}>
          <span>{question.minLabel}</span><span>{question.maxLabel}</span>
        </div>
      )}
    </>
  );
}

function LikertChart({ summary }) {
  const colors = divergingColors(summary.columns.length);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {summary.rows.map((r) => (
        <div key={r.id}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, marginBottom: 6 }}>
            <span style={{ color: C.ink }}>{r.label}</span>
            <span style={{ color: C.ink2, whiteSpace: "nowrap" }}>avg <strong style={{ color: C.ink }}>{r.avg === null ? "—" : r.avg.toFixed(2)}</strong> / {summary.columns.length}</span>
          </div>
          <div style={{ display: "flex", height: 22, borderRadius: 4, overflow: "hidden", gap: 2 }}>
            {r.total === 0 && <div style={{ flex: 1, background: "#F1F0EE" }} />}
            {r.counts.map((c, i) => c > 0 && (
              <div key={summary.columns[i].id} title={`${summary.columns[i].label}: ${c} (${r.pcts[i].toFixed(1)}%)`} style={{ width: `${r.pcts[i]}%`, background: colors[i], color: colors[i] === VIZ.mid || colors[i] === VIZ.pos[0] || colors[i] === VIZ.neg[0] ? C.ink : "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {r.pcts[i] >= 10 ? `${Math.round(r.pcts[i])}%` : ""}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 2 }}>
        {summary.columns.map((c, i) => (
          <span key={c.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.ink2 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: colors[i] }} /> {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function TextAnswers({ summary, anonymous, labelFor }) {
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const list = useMemo(() => summary.texts.filter((t) => !q || t.text.toLowerCase().includes(q.toLowerCase())), [summary.texts, q]);
  const shown = showAll ? list : list.slice(0, 6);
  return (
    <div>
      {summary.words.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Frequent words</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {summary.words.map((w) => (
              <button key={w.word} type="button" onClick={() => setQ(q === w.word ? "" : w.word)} style={{ border: `1px solid ${q === w.word ? C.accent : C.line}`, background: q === w.word ? "#F0F6FF" : "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 12 + Math.min(4, w.count - 1), color: C.ink, cursor: "pointer", fontFamily: "inherit" }}>
                {w.word} <span style={{ color: C.muted, fontSize: 11 }}>{w.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {summary.texts.length > 6 && (
        <div style={{ position: "relative", marginBottom: 10, maxWidth: 320 }}>
          <Search24Regular style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: C.muted }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search answers" style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px 7px 28px", border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13, outline: "none" }} />
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {shown.map((t) => (
          <div key={t.response.id} style={{ background: C.soft, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: C.ink, lineHeight: 1.6, borderLeft: `3px solid ${VIZ.barMuted}` }}>
            <div style={{ whiteSpace: "pre-line" }}>{t.text}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{anonymous ? "Anonymous" : labelFor(t.response)}{t.response.department ? ` · ${t.response.department}` : ""} · {formatDate(t.response.submittedAt)}</div>
          </div>
        ))}
        {list.length === 0 && <Text style={{ fontSize: 13, color: C.muted }}>{summary.texts.length ? "No answers match." : "No written answers yet."}</Text>}
      </div>
      {list.length > 6 && (
        <button type="button" onClick={() => setShowAll(!showAll)} style={{ marginTop: 10, background: "none", border: "none", color: C.brand, fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
          {showAll ? "Show fewer" : `Show all ${list.length} answers`}
        </button>
      )}
    </div>
  );
}

function DataTable({ summary }) {
  let rows = [];
  if (summary.kind === "choice" || summary.kind === "binary") rows = summary.rows.map((r) => [r.label, r.count, `${r.pct.toFixed(1)}%`]);
  else if (summary.kind === "scale") rows = summary.dist.map((d) => [d.label, d.count, `${d.pct.toFixed(1)}%`]);
  else if (summary.kind === "likert") rows = summary.rows.map((r) => [r.label, r.total, r.avg === null ? "—" : r.avg.toFixed(2)]);
  else if (summary.kind === "date") rows = summary.rows.map((r) => [formatDate(r.label), r.count, ""]);
  const head = summary.kind === "likert" ? ["Statement", "Answers", "Average"] : ["Answer", "Count", "Percent"];
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>{head.map((h, i) => <th key={h} style={{ textAlign: i ? "right" : "left", padding: "6px 8px", fontSize: 11, color: C.muted, textTransform: "uppercase", borderBottom: `1px solid ${C.line}` }}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[0]}>{r.map((v, i) => <td key={i} style={{ textAlign: i ? "right" : "left", padding: "7px 8px", borderBottom: "1px solid #F5F4F2", color: C.ink }}>{v}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

export function QuestionResultCard({ question, number, summary, anonymous, labelFor, totalResponses }) {
  const [asTable, setAsTable] = useState(false);
  const tableable = ["choice", "binary", "scale", "likert", "date"].includes(summary.kind);
  const answeredPct = totalResponses ? (summary.answered / totalResponses) * 100 : 0;

  let body = null;
  if (asTable && tableable) body = <DataTable summary={summary} />;
  else if (summary.kind === "choice") {
    body = (
      <>
        <HBarList rows={summary.rows} />
        {question.type === "multi_choice" && <Text style={{ fontSize: 11, color: C.muted, display: "block", marginTop: 8 }}>Respondents could pick more than one option, so percentages can exceed 100% in total.</Text>}
        {summary.otherTexts.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>“Other” answers</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {summary.otherTexts.map((t, i) => <span key={i} style={{ fontSize: 12, background: C.soft, border: `1px solid ${C.line}`, borderRadius: 6, padding: "3px 8px" }}>{t}</span>)}
            </div>
          </div>
        )}
      </>
    );
  } else if (summary.kind === "binary") {
    body = <SegmentBar height={34} segments={[{ label: "Yes", count: summary.rows[0].count, color: VIZ.yes }, { label: "No", count: summary.rows[1].count, color: VIZ.no }]} />;
  } else if (summary.kind === "scale") body = <ScaleChart summary={summary} question={question} />;
  else if (summary.kind === "likert") body = <LikertChart summary={summary} />;
  else if (summary.kind === "number") {
    body = (
      <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
        <Stat label="Average" value={summary.avg === null ? "—" : summary.avg.toLocaleString(undefined, { maximumFractionDigits: 2 })} />
        <Stat label="Median" value={summary.median ?? "—"} />
        <Stat label="Min" value={summary.min ?? "—"} />
        <Stat label="Max" value={summary.max ?? "—"} />
        <Stat label="Total" value={summary.sum.toLocaleString()} />
      </div>
    );
  } else if (summary.kind === "date") {
    body = summary.rows.length ? <HBarList rows={summary.rows.map((r) => ({ ...r, label: formatDate(r.label), pct: summary.answered ? (r.count / summary.answered) * 100 : 0 }))} highlightTop={false} /> : null;
  } else body = <TextAnswers summary={summary} anonymous={anonymous} labelFor={labelFor} />;

  return (
    <div style={{ ...card, padding: "18px 20px" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: C.accent, paddingTop: 2 }}>Q{number}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 15, fontWeight: 700, color: C.ink, display: "block", lineHeight: 1.4 }}>{question.title}</Text>
          <Text style={{ fontSize: 12, color: C.muted }}>
            {TYPE_META[question.type]?.label} · {summary.answered} answered ({Math.round(answeredPct)}%){summary.skipped ? ` · ${summary.skipped} skipped` : ""}
            {question.showIf ? " · conditional" : ""}
          </Text>
        </div>
        {tableable && (
          <button type="button" onClick={() => setAsTable(!asTable)} title={asTable ? "Show chart" : "Show table"} style={{ border: `1px solid ${C.line}`, background: "#fff", borderRadius: 8, padding: "5px 8px", cursor: "pointer", color: C.ink2, display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontFamily: "inherit" }}>
            {asTable ? <DataPie24Regular style={{ fontSize: 16 }} /> : <Table24Regular style={{ fontSize: 16 }} />} {asTable ? "Chart" : "Table"}
          </button>
        )}
      </div>
      {summary.answered === 0 ? <Text style={{ fontSize: 13, color: C.muted }}>No answers yet.</Text> : body}
    </div>
  );
}

export { HBarList, SegmentBar, ChartTooltip };
